import { useState, useEffect } from 'react'
import {
    ScrollText,
    Search,
    RefreshCw,
    Clock,
    CheckCircle2,
    XCircle,
    Loader2,
    AlertTriangle,
    FileText,
    ChevronRight,
    Filter,
    Play,
    FolderTree,
    Eye,
    BarChart3,
    Users,
    Package,
    StopCircle,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { useWorkspace } from '@/contexts/WorkspaceContext'
import { trpc } from '@/lib/trpc'
import type { JobLog, JobLogStats, JobLogStatus } from '@/types'

const STATUS_CONFIG: Record<string, { icon: typeof Clock; color: string; bg: string; label: string; animate?: boolean }> = {
    pending: { icon: Clock, color: 'text-muted-foreground', bg: 'bg-muted', label: 'Pending' },
    running: { icon: Loader2, color: 'text-blue-500', bg: 'bg-blue-500/10', label: 'Running', animate: true },
    completed: { icon: CheckCircle2, color: 'text-green-500', bg: 'bg-green-500/10', label: 'Completed' },
    failed: { icon: XCircle, color: 'text-destructive', bg: 'bg-destructive/10', label: 'Failed' },
}

function formatDuration(ms?: number): string {
    if (!ms) return '-'
    if (ms < 1000) return `${ms}ms`
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
    return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`
}

function formatDate(date: string): string {
    return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).format(new Date(date))
}

function JobLogRow({ log, onClick }: { log: JobLog; onClick: () => void }) {
    const config = STATUS_CONFIG[log.status]
    const Icon = config.icon

    return (
        <div
            onClick={onClick}
            className="flex items-center gap-4 p-4 border-b border-border hover:bg-muted/50 cursor-pointer transition-colors"
        >
            <div className={cn('p-2 rounded-lg', config.bg)}>
                <Icon className={cn('h-4 w-4', config.color, config.animate && 'animate-spin')} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-foreground truncate">
                        {log.jobType}
                    </span>
                    <Badge variant="outline" className="text-xs">
                        #{log.attemptNumber}
                    </Badge>
                </div>
                <div className="text-sm text-muted-foreground truncate">
                    {log.jobId && <span className="font-mono text-xs">{log.jobId.slice(0, 8)}...</span>}
                    {log.errorMessage && (
                        <span className="text-destructive"> • {log.errorMessage.slice(0, 50)}...</span>
                    )}
                </div>
            </div>
            <div className="text-right">
                <div className="text-sm text-muted-foreground">{formatDate(log.startedAt)}</div>
                <div className="text-xs text-muted-foreground">{formatDuration(log.durationMs)}</div>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </div>
    )
}

function StatsCard({ stats, isLoading }: { stats?: JobLogStats; isLoading: boolean }) {
    if (isLoading) {
        return (
            <Card>
                <CardContent className="p-4">
                    <div className="flex items-center justify-center h-16">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                </CardContent>
            </Card>
        )
    }

    if (!stats) return null

    return (
        <Card className="mb-6">
            <CardContent className="px-4 pb-4 pt-2">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div>
                        <div className="text-2xl font-bold text-foreground">{stats.total}</div>
                        <div className="text-sm text-muted-foreground">Total Jobs</div>
                    </div>
                    <div>
                        <div className="text-2xl font-bold text-green-500">{stats.completed}</div>
                        <div className="text-sm text-muted-foreground">Completed</div>
                    </div>
                    <div>
                        <div className="text-2xl font-bold text-destructive">{stats.failed}</div>
                        <div className="text-sm text-muted-foreground">Failed</div>
                    </div>
                    <div>
                        <div className="text-2xl font-bold text-blue-500">{stats.running}</div>
                        <div className="text-sm text-muted-foreground">Running</div>
                    </div>
                    <div>
                        <div className="text-2xl font-bold text-foreground">
                            {stats.errorRate.toFixed(1)}%
                        </div>
                        <div className="text-sm text-muted-foreground">Error Rate</div>
                    </div>
                </div>
            </CardContent>
        </Card>
    )
}

function JobLogDetail({ log, onClose }: { log: JobLog; onClose: () => void }) {
    const config = STATUS_CONFIG[log.status]
    const Icon = config.icon

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-3">
                        <div className={cn('p-2 rounded-lg', config.bg)}>
                            <Icon className={cn('h-5 w-5', config.color)} />
                        </div>
                        <div>
                            <div className="font-semibold">{log.jobType}</div>
                            <div className="text-sm font-normal text-muted-foreground">
                                {log.jobId && <span className="font-mono">{log.jobId}</span>}
                            </div>
                        </div>
                    </DialogTitle>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto space-y-4">
                    {/* Metadata */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted/30 rounded-lg">
                        <div>
                            <div className="text-xs text-muted-foreground">Status</div>
                            <Badge className={cn('mt-1', config.bg, config.color)} variant="outline">
                                {config.label}
                            </Badge>
                        </div>
                        <div>
                            <div className="text-xs text-muted-foreground">Started At</div>
                            <div className="text-sm font-medium">{formatDate(log.startedAt)}</div>
                        </div>
                        <div>
                            <div className="text-xs text-muted-foreground">Duration</div>
                            <div className="text-sm font-medium">{formatDuration(log.durationMs)}</div>
                        </div>
                        <div>
                            <div className="text-xs text-muted-foreground">Attempt</div>
                            <div className="text-sm font-medium">{log.attemptNumber} / {log.maxAttempts}</div>
                        </div>
                    </div>

                    {/* Job Data */}
                    {Object.keys(log.jobData).length > 0 && (
                        <div>
                            <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                                <FileText className="h-4 w-4" />
                                Job Data
                            </h4>
                            <pre className="p-3 bg-muted/50 rounded-lg text-xs overflow-x-auto font-mono">
                                {JSON.stringify(log.jobData, null, 2)}
                            </pre>
                        </div>
                    )}

                    {/* Error */}
                    {log.errorMessage && (
                        <div>
                            <h4 className="text-sm font-medium mb-2 flex items-center gap-2 text-destructive">
                                <AlertTriangle className="h-4 w-4" />
                                Error
                            </h4>
                            <div className="p-3 bg-destructive/10 rounded-lg">
                                <p className="text-sm text-destructive font-medium mb-2">
                                    {log.errorMessage}
                                </p>
                                {log.errorStack && (
                                    <pre className="text-xs text-muted-foreground overflow-x-auto whitespace-pre-wrap font-mono">
                                        {log.errorStack}
                                    </pre>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Log Entries */}
                    {log.logEntries.length > 0 && (
                        <div>
                            <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                                <ScrollText className="h-4 w-4" />
                                Log Entries ({log.logEntries.length})
                            </h4>
                            <div className="border border-border rounded-lg overflow-hidden">
                                {log.logEntries.map((entry, idx) => (
                                    <div
                                        key={idx}
                                        className={cn(
                                            'p-2 text-xs font-mono border-b last:border-0',
                                            entry.level === 'error' && 'bg-destructive/5',
                                            entry.level === 'warn' && 'bg-yellow-500/5'
                                        )}
                                    >
                                        <span className="text-muted-foreground">
                                            {new Date(entry.timestamp).toLocaleTimeString()}
                                        </span>
                                        <span
                                            className={cn(
                                                'ml-2 uppercase',
                                                entry.level === 'error' && 'text-destructive',
                                                entry.level === 'warn' && 'text-yellow-500',
                                                entry.level === 'info' && 'text-blue-500',
                                                entry.level === 'debug' && 'text-muted-foreground'
                                            )}
                                        >
                                            [{entry.level}]
                                        </span>
                                        <span className="ml-2 text-foreground">{entry.message}</span>
                                        {entry.data && (
                                            <pre className="mt-1 text-muted-foreground">
                                                {JSON.stringify(entry.data, null, 2)}
                                            </pre>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Result Summary */}
                    {log.resultSummary && (
                        <div>
                            <h4 className="text-sm font-medium mb-2 flex items-center gap-2 text-green-500">
                                <CheckCircle2 className="h-4 w-4" />
                                Result
                            </h4>
                            <pre className="p-3 bg-green-500/10 rounded-lg text-xs overflow-x-auto font-mono">
                                {JSON.stringify(log.resultSummary, null, 2)}
                            </pre>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    )
}

export function JobLogsPage() {
    const { currentWorkspace } = useWorkspace()
    const [logs, setLogs] = useState<JobLog[]>([])
    const [stats, setStats] = useState<JobLogStats | undefined>()
    const [jobTypes, setJobTypes] = useState<string[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [isStatsLoading, setIsStatsLoading] = useState(true)
    const [selectedLog, setSelectedLog] = useState<JobLog | null>(null)

    // Filters
    const [search, setSearch] = useState('')
    const [statusFilter, setStatusFilter] = useState<string>('all')
    const [jobTypeFilter, setJobTypeFilter] = useState<string>('all')
    const [autoRefresh, setAutoRefresh] = useState(false)
    const [scrapeLoading, setScrapeLoading] = useState<string | null>(null)
    const [scrapeMessage, setScrapeMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
    const [stopLoading, setStopLoading] = useState<string | null>(null)
    const [stopMessage, setStopMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

    const workspaceId = currentWorkspace?.id

    // tRPC utils for imperative query calls
    const utils = trpc.useUtils()

    // tRPC mutations for triggering jobs
    const triggerCategoryScrapeMutation = trpc.categories.scrape.useMutation()
    const triggerVisibilityScrapeMutation = trpc.visibility.triggerScrape.useMutation()
    const triggerVisibilityAggregationMutation = trpc.visibility.triggerAggregation.useMutation()
    const triggerCompetitorScrapeMutation = trpc.competitorScrape.trigger.useMutation()
    const triggerAvailabilityScrapeMutation = trpc.availability.triggerScrape.useMutation()

    // tRPC mutations for clearing queues
    const clearCompetitorQueueMutation = trpc.competitorScrape.clearQueue.useMutation()
    const clearVisibilityQueueMutation = trpc.visibility.clearQueue.useMutation()
    const clearAvailabilityQueueMutation = trpc.availability.clearQueue.useMutation()

    const handleTriggerJob = async (jobKey: string, triggerFn: () => Promise<unknown>) => {
        if (!workspaceId) return
        setScrapeLoading(jobKey)
        setScrapeMessage(null)
        try {
            await triggerFn()
            setScrapeMessage({ type: 'success', text: 'Job triggered! Check logs below for progress.' })
            // Refresh the logs after a short delay
            setTimeout(() => {
                fetchLogs()
                fetchStats()
            }, 2000)
        } catch {
            setScrapeMessage({ type: 'error', text: 'Failed to trigger job. Please try again.' })
        } finally {
            setScrapeLoading(null)
        }
    }

    const handleStopJob = async (jobKey: string, stopFn: () => Promise<unknown>) => {
        setStopLoading(jobKey)
        setStopMessage(null)
        try {
            await stopFn()
            setStopMessage({ type: 'success', text: 'Jobs stopped! All pending and active jobs have been cleared.' })
            // Refresh the logs and stats after stopping
            setTimeout(() => {
                fetchLogs()
                fetchStats()
            }, 1000)
        } catch {
            setStopMessage({ type: 'error', text: 'Failed to stop jobs. Please try again.' })
        } finally {
            setStopLoading(null)
        }
    }

    const fetchLogs = async () => {
        if (!workspaceId) return

        try {
            const result = await utils.jobLogs.search.fetch({
                workspaceId,
                status: statusFilter !== 'all' ? (statusFilter as JobLogStatus) : undefined,
                jobType: jobTypeFilter !== 'all' ? jobTypeFilter : undefined,
                search: search || undefined,
                limit: 50,
                offset: 0,
            })
            setLogs(result.logs)
        } catch (error) {
            console.error('Failed to fetch job logs:', error)
        } finally {
            setIsLoading(false)
        }
    }

    const fetchStats = async () => {
        if (!workspaceId) return

        try {
            const result = await utils.jobLogs.stats.fetch({ workspaceId, days: 7 })
            if (result) {
                setStats(result)
            }
        } catch (error) {
            console.error('Failed to fetch job log stats:', error)
        } finally {
            setIsStatsLoading(false)
        }
    }

    const fetchJobTypes = async () => {
        if (!workspaceId) return

        try {
            const result = await utils.jobLogs.types.fetch({ workspaceId })
            setJobTypes(result.types)
        } catch (error) {
            console.error('Failed to fetch job types:', error)
        }
    }

    const loadAll = async () => {
        setIsLoading(true)
        await Promise.all([fetchLogs(), fetchStats(), fetchJobTypes()])
    }

    useEffect(() => {
        loadAll()
    }, [workspaceId, statusFilter, jobTypeFilter])

    useEffect(() => {
        if (autoRefresh) {
            const interval = setInterval(fetchLogs, 5000)
            return () => clearInterval(interval)
        }
    }, [autoRefresh, workspaceId, statusFilter, jobTypeFilter])

    const handleSearch = () => {
        setIsLoading(true)
        fetchLogs()
    }

    return (
        <div className="p-6 md:p-8 w-full animate-fade-in">
            <div className="max-w-[1200px] mx-auto">
                {/* Header */}
                <div className="mb-6">
                    <div className="flex items-center gap-3 mb-2">
                        <ScrollText className="h-8 w-8 text-primary" />
                        <h1 className="app-page-title">Job Logs</h1>
                    </div>
                    <p className="app-page-subtitle">
                        View and debug background job execution history for scraping, calculations, and more.
                    </p>
                </div>

                {/* Stats */}
                <StatsCard stats={stats} isLoading={isStatsLoading} />

                {/* Run Jobs */}
                <Card className="mb-6">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                            <Play className="h-4 w-4" />
                            Run Jobs
                        </CardTitle>
                        <CardDescription className="text-xs">
                            Trigger background jobs on demand for this workspace.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="pb-4">
                        <div className="flex flex-wrap gap-3">
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={scrapeLoading !== null}
                                onClick={() => handleTriggerJob('categories', () => triggerCategoryScrapeMutation.mutateAsync({ workspaceId: workspaceId! }))}
                            >
                                {scrapeLoading === 'categories' ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                    <FolderTree className="h-4 w-4 mr-2" />
                                )}
                                Scrape Categories
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={scrapeLoading !== null}
                                onClick={() => handleTriggerJob('visibility', () => triggerVisibilityScrapeMutation.mutateAsync({ workspaceId: workspaceId! }))}
                            >
                                {scrapeLoading === 'visibility' ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                    <Eye className="h-4 w-4 mr-2" />
                                )}
                                Scrape Visibility
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={scrapeLoading !== null}
                                onClick={() => handleTriggerJob('visibility-aggregation', () => triggerVisibilityAggregationMutation.mutateAsync({ workspaceId: workspaceId! }))}
                            >
                                {scrapeLoading === 'visibility-aggregation' ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                    <BarChart3 className="h-4 w-4 mr-2" />
                                )}
                                Aggregate Visibility
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={scrapeLoading !== null}
                                onClick={() => handleTriggerJob('availability', () => triggerAvailabilityScrapeMutation.mutateAsync({ workspaceId: workspaceId! }))}
                            >
                                {scrapeLoading === 'availability' ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                    <Package className="h-4 w-4 mr-2" />
                                )}
                                Scrape Availability
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={scrapeLoading !== null}
                                onClick={() => handleTriggerJob('competitor', () => triggerCompetitorScrapeMutation.mutateAsync({ workspaceId: workspaceId! }))}
                            >
                                {scrapeLoading === 'competitor' ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                    <Users className="h-4 w-4 mr-2" />
                                )}
                                Scrape Competitors
                            </Button>
                        </div>
                        {scrapeMessage && (
                            <div className={cn(
                                'mt-3 text-sm px-3 py-2 rounded-lg',
                                scrapeMessage.type === 'success'
                                    ? 'bg-green-500/10 text-green-500'
                                    : 'bg-destructive/10 text-destructive'
                            )}>
                                {scrapeMessage.text}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Stop Jobs */}
                <Card className="mb-6 border-destructive/30">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2 text-destructive">
                            <StopCircle className="h-4 w-4" />
                            Force Stop Jobs
                        </CardTitle>
                        <CardDescription className="text-xs">
                            Immediately stop all pending and active jobs for a specific scraper. Use with caution.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="pb-4">
                        <div className="flex flex-wrap gap-3">
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={stopLoading !== null}
                                onClick={() => handleStopJob('visibility', () => clearVisibilityQueueMutation.mutateAsync())}
                                className="border-destructive/50 text-destructive hover:bg-destructive hover:text-destructive-foreground"
                            >
                                {stopLoading === 'visibility' ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                    <StopCircle className="h-4 w-4 mr-2" />
                                )}
                                Stop Visibility
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={stopLoading !== null}
                                onClick={() => handleStopJob('availability', () => clearAvailabilityQueueMutation.mutateAsync())}
                                className="border-destructive/50 text-destructive hover:bg-destructive hover:text-destructive-foreground"
                            >
                                {stopLoading === 'availability' ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                    <StopCircle className="h-4 w-4 mr-2" />
                                )}
                                Stop Availability
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={stopLoading !== null}
                                onClick={() => handleStopJob('competitor', () => clearCompetitorQueueMutation.mutateAsync())}
                                className="border-destructive/50 text-destructive hover:bg-destructive hover:text-destructive-foreground"
                            >
                                {stopLoading === 'competitor' ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                    <StopCircle className="h-4 w-4 mr-2" />
                                )}
                                Stop Competitors
                            </Button>
                        </div>
                        {stopMessage && (
                            <div className={cn(
                                'mt-3 text-sm px-3 py-2 rounded-lg',
                                stopMessage.type === 'success'
                                    ? 'bg-green-500/10 text-green-500'
                                    : 'bg-destructive/10 text-destructive'
                            )}>
                                {stopMessage.text}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Filters */}
                <Card className="mb-6">
                    <CardContent className="px-4 pb-4 pt-2">
                        <div className="flex flex-col md:flex-row gap-4">
                            <div className="flex-1 relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                    placeholder="Search by job type or error message..."
                                    className="pl-9"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                />
                            </div>
                            <Select value={statusFilter} onValueChange={setStatusFilter}>
                                <SelectTrigger className="w-[150px]">
                                    <Filter className="h-4 w-4 mr-2" />
                                    <SelectValue placeholder="Status" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Status</SelectItem>
                                    <SelectItem value="pending">Pending</SelectItem>
                                    <SelectItem value="running">Running</SelectItem>
                                    <SelectItem value="completed">Completed</SelectItem>
                                    <SelectItem value="failed">Failed</SelectItem>
                                </SelectContent>
                            </Select>
                            <Select value={jobTypeFilter} onValueChange={setJobTypeFilter}>
                                <SelectTrigger className="w-[180px]">
                                    <FileText className="h-4 w-4 mr-2" />
                                    <SelectValue placeholder="Job Type" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Types</SelectItem>
                                    {jobTypes.map((type) => (
                                        <SelectItem key={type} value={type}>
                                            {type}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Button
                                variant={autoRefresh ? 'default' : 'outline'}
                                onClick={() => setAutoRefresh(!autoRefresh)}
                            >
                                <RefreshCw className={cn('h-4 w-4 mr-2', autoRefresh && 'animate-spin')} />
                                {autoRefresh ? 'Live' : 'Auto-refresh'}
                            </Button>
                            <Button variant="outline" onClick={loadAll}>
                                <RefreshCw className="h-4 w-4" />
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* Logs Table */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg">Recent Jobs</CardTitle>
                        <CardDescription>
                            Click on a job to view full details and log entries
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="p-0">
                        {isLoading ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                            </div>
                        ) : logs.length === 0 ? (
                            <div className="text-center py-12">
                                <ScrollText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                                <p className="text-muted-foreground">No job logs found</p>
                                <p className="text-sm text-muted-foreground">
                                    Jobs will appear here once background tasks start running.
                                </p>
                            </div>
                        ) : (
                            <div className="divide-y divide-border">
                                {logs.map((log) => (
                                    <JobLogRow
                                        key={log.id}
                                        log={log}
                                        onClick={() => setSelectedLog(log)}
                                    />
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Detail Modal */}
            {selectedLog && (
                <JobLogDetail log={selectedLog} onClose={() => setSelectedLog(null)} />
            )}
        </div>
    )
}
